import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import cx from 'classnames';

import { openJourneyDetail } from 'redux/modules/journeys/effects';
import { useSelectedTasksContext } from 'modules/tasks/lists/context';

import Task from '../task';
import OrderId from './OrderId';
import Owner from './Owner';
import { ReactComponent as GirdedExclamation } from 'assets/icons/girded-exclamation.svg';

import GroupSelector from './GroupSelector';
import { getDetailSidebarTab, getIdFromUrl } from './utils';
import ServiceTag, { ServiceTagFleetService, SKINS, TagGroup } from 'components/ServiceTag';
import { VEHICLE_IMAGES } from 'components/VehicleImage';

const t = {
  ftb: 'FTB',
  prio: 'Prio',
  recovering: 'Rec.',
  ontruck24: '24h',
};

const TaskGroup = ({
  id,
  tasks,
  owner,
  shipper_is_prio,
  has_ftb_shipper,
  has_recovering_shipper,
  has_xdock_shipper_order,
  driver_is_fleet_service,
  journeyStatus,
  className,
  driver,
  vehicle_type,
}) => {
  const [openJourney, setOpenJourney] = useState(null);
  const { selected, setSelected } = useSelectedTasksContext();
  const dispatch = useDispatch();
  const location = useLocation();

  const isOpen = Number(openJourney) === id;
  const isSelected = (selected || []).includes(id);

  const handleOpenDetail = useCallback(() => {
    dispatch(
      openJourneyDetail({
        id,
        tab: '/tasks',
        sidebarTab: getDetailSidebarTab({ tasks, journeyStatus }),
      })
    );
  }, [dispatch, id, tasks, journeyStatus]);

  useEffect(() => {
    setOpenJourney(getIdFromUrl(location.pathname));
  }, [location]);

  return (
    <div data-testid="taskGroup" className={className} onClick={handleOpenDetail}>
      <div className={cx('header', { 'header--selected': isSelected, 'header--open': isOpen })}>
        <div className="header__container">
          <div className="header__group">
            <span className="header__group__id">
              <OrderId id={id} isOpen={isOpen} isSelected={isSelected} />
            </span>
            <TagGroup>
              {shipper_is_prio && <ServiceTag skin={SKINS.primary} text={t.prio} size="S" />}
              {has_ftb_shipper && <ServiceTag skin={SKINS.success} text={t.ftb} size="S" />}
              {has_recovering_shipper && (
                <ServiceTag skin={SKINS.accent} text={t.recovering} size="S" />
              )}
              {has_xdock_shipper_order && (
                <ServiceTag skin={SKINS.deepPurple} text={t.ontruck24} size="S" />
              )}
              {driver_is_fleet_service && <ServiceTagFleetService size="S" />}
            </TagGroup>
          </div>

          <div className="header__group">
            <span className="header__group__owner">
              <Owner owner={owner} journeyId={id} isOpen={isOpen} isSelected={isSelected} />
            </span>
            <span className="header__group__selector">
              <GroupSelector journeyId={id} isSelected={isSelected} setSelected={setSelected} />
            </span>
          </div>
        </div>
        <div className="header__driver-info">
          {vehicle_type &&
            (VEHICLE_IMAGES[vehicle_type] ? (
              <span role="img" aria-label={vehicle_type}>
                {VEHICLE_IMAGES[vehicle_type]({ size: '25px' })}
              </span>
            ) : (
              <span className="girded-exclamation">
                <GirdedExclamation />
              </span>
            ))}
          {driver && <span className="header__driver-info__name">{driver.toLowerCase()}</span>}
        </div>
      </div>

      <div className="journey-tasks">
        {tasks?.map((task) => (
          <Task
            id={task?.id}
            key={`${task?.id}-${task?.status}`}
            status={task?.status}
            comment={task?.comment}
            commentData={task?.comment_data}
            creationTime={task?.created_at}
            taskType={task?.task_type}
          />
        ))}
      </div>
    </div>
  );
};

export default TaskGroup;
